import { useEffect, useRef, useState, useCallback } from "react";
import {
  INACTIVITY_WARNING_TIMEOUT,
  INACTIVITY_AUTO_LOGOUT_TIMEOUT,
} from "../utils/constant";

interface UseInactivityTimeoutOptions {
  enabled: boolean;
  onWarning: () => void;
  onTimeout: () => void; // Called when inactivity timeout completes (warning countdown reaches 0)
  warningTimeout?: number;
  logoutTimeout?: number;
}

/**
 * Custom hook to track user inactivity and trigger warning/logout
 * @param options - Configuration options
 * @returns Object with warning state and reset function
 */
export const useInactivityTimeout = ({
  enabled,
  onWarning,
  onTimeout,
  warningTimeout = INACTIVITY_WARNING_TIMEOUT,
  logoutTimeout = INACTIVITY_AUTO_LOGOUT_TIMEOUT,
}: UseInactivityTimeoutOptions) => {
  const [showWarning, setShowWarning] = useState(false);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isWarningShownRef = useRef(false);
  const mousemoveThrottleRef = useRef<NodeJS.Timeout | null>(null);
  const warningStartTimeRef = useRef<number | null>(null); // Track when warning timer started
  const isTabVisibleRef = useRef(true); // Track if tab is visible

  // Reset all timers
  const resetTimers = useCallback(() => {
    // Don't reset if warning is already shown (user needs to click "Stay Logged In")
    if (isWarningShownRef.current) {
      return;
    }

    // Clear existing timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    // Reset warning state
    setShowWarning(false);
    isWarningShownRef.current = false;
    warningStartTimeRef.current = null; // Clear start time before setting new timer

    if (!enabled) {
      return;
    }

    // Set warning timer
    warningStartTimeRef.current = Date.now(); // Track when timer started
    console.log(`⏰ Setting inactivity warning timer for ${warningTimeout / 1000} seconds`);
    
    const checkAndTriggerWarning = () => {
      if (!warningStartTimeRef.current) return;
      
      const elapsed = Date.now() - warningStartTimeRef.current;
      const remaining = warningTimeout - elapsed;
      
      if (remaining <= 0) {
        // Timeout has elapsed
        console.log("⏰ Inactivity warning triggered - showing modal");
        setShowWarning(true);
        isWarningShownRef.current = true;
        onWarning();
        warningStartTimeRef.current = Date.now(); // Track when warning started

        // Set timeout callback after warning countdown
        const checkAndTriggerTimeout = () => {
          if (!warningStartTimeRef.current) return;
          const warningElapsed = Date.now() - warningStartTimeRef.current;
          const timeoutRemaining = logoutTimeout - warningElapsed;
          
          if (timeoutRemaining <= 0) {
            console.log("⏰ Inactivity timeout completed - showing session expired");
            setShowWarning(false);
            onTimeout();
            warningStartTimeRef.current = null;
          } else {
            // Schedule next check
            logoutTimerRef.current = setTimeout(checkAndTriggerTimeout, Math.min(timeoutRemaining, 1000));
          }
        };
        
        logoutTimerRef.current = setTimeout(checkAndTriggerTimeout, Math.min(logoutTimeout, 1000));
      } else {
        // Schedule next check
        warningTimerRef.current = setTimeout(checkAndTriggerWarning, Math.min(remaining, 1000));
      }
    };
    
    warningTimerRef.current = setTimeout(checkAndTriggerWarning, Math.min(warningTimeout, 1000));
  }, [enabled, onWarning, onTimeout, warningTimeout, logoutTimeout]);

  // Function to manually reset timers (e.g., when user clicks "Stay Logged In")
  // This function properly clears the warning state first
  const reset = useCallback(() => {
    console.log("🔄 Manually resetting inactivity timer");
    
    // Clear all timers first
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    // Reset warning state immediately
    isWarningShownRef.current = false;
    setShowWarning(false);
    warningStartTimeRef.current = null; // Clear start time

    // Then reset timers if enabled
    if (enabled) {
      // Use setTimeout to ensure state update completes first
      setTimeout(() => {
        resetTimers();
      }, 0);
    }
  }, [enabled, resetTimers]);

  // Handle user activity events
  useEffect(() => {
    if (!enabled) {
      // Clear timers if disabled
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      setShowWarning(false);
      isWarningShownRef.current = false;
      warningStartTimeRef.current = null; // Clear start time when disabled
      return;
    }

    // List of events that indicate user activity
    const events = [
      "mousedown",
      "mousemove", // Mouse cursor movement (throttled)
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
      "wheel", // Mouse wheel events
    ];

    // Throttle mousemove events to prevent constant resets (only reset every 2 seconds)
    const MOUSEMOVE_THROTTLE_MS = 2000; // Only reset timer every 2 seconds for mouse movement

    // Wrapper function to prevent reset when warning is shown
    const handleActivity = (event: Event) => {
      // Don't reset if warning is shown - user must click "Stay Logged In"
      if (isWarningShownRef.current) {
        return;
      }
      // Don't reset for clicks inside the modal
      const target = event.target as HTMLElement;
      if (target?.closest('[data-inactivity-modal]')) {
        return;
      }

      // Special handling for mousemove - throttle it to prevent constant resets
      if (event.type === "mousemove") {
        if (mousemoveThrottleRef.current) {
          return; // Skip if throttle is active
        }
        // Set throttle - only reset timer every 2 seconds for mouse movement
        mousemoveThrottleRef.current = setTimeout(() => {
          mousemoveThrottleRef.current = null;
        }, MOUSEMOVE_THROTTLE_MS);
      }

      resetTimers();
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Handle page visibility changes (tab switch, window minimize, etc.)
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isTabVisibleRef.current = isVisible;
      
      if (isVisible) {
        // Tab became visible - check if timeout has elapsed while hidden
        console.log("👁️ Tab became visible - checking inactivity status");
        
        if (warningStartTimeRef.current && !isWarningShownRef.current) {
          // Check if warning timeout has elapsed
          const elapsed = Date.now() - warningStartTimeRef.current;
          if (elapsed >= warningTimeout) {
            // Timeout elapsed while tab was hidden - trigger warning immediately
            console.log("⏰ Inactivity timeout elapsed while tab was hidden - showing warning");
            if (warningTimerRef.current) {
              clearTimeout(warningTimerRef.current);
              warningTimerRef.current = null;
            }
            setShowWarning(true);
            isWarningShownRef.current = true;
            onWarning();
            warningStartTimeRef.current = Date.now();
            
            // Start logout timer
            const checkAndTriggerTimeout = () => {
              if (!warningStartTimeRef.current) return;
              const warningElapsed = Date.now() - warningStartTimeRef.current;
              const timeoutRemaining = logoutTimeout - warningElapsed;
              
              if (timeoutRemaining <= 0) {
                console.log("⏰ Inactivity timeout completed - showing session expired");
                setShowWarning(false);
                onTimeout();
                warningStartTimeRef.current = null;
              } else {
                logoutTimerRef.current = setTimeout(checkAndTriggerTimeout, Math.min(timeoutRemaining, 1000));
              }
            };
            
            logoutTimerRef.current = setTimeout(checkAndTriggerTimeout, Math.min(logoutTimeout, 1000));
          }
        } else if (warningStartTimeRef.current && isWarningShownRef.current) {
          // Warning is already shown - check if logout timeout has elapsed
          const warningElapsed = Date.now() - warningStartTimeRef.current;
          if (warningElapsed >= logoutTimeout) {
            // Logout timeout elapsed while tab was hidden
            console.log("⏰ Logout timeout elapsed while tab was hidden - showing session expired");
            if (logoutTimerRef.current) {
              clearTimeout(logoutTimerRef.current);
              logoutTimerRef.current = null;
            }
            setShowWarning(false);
            onTimeout();
            warningStartTimeRef.current = null;
          }
        }
      } else {
        // Tab became hidden - timer continues running, no action needed
        console.log("👁️ Tab became hidden - inactivity timer continues");
      }
    };

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initialize timer
    console.log("🔄 Initializing inactivity timeout with", warningTimeout / 1000, "seconds warning timeout");
    resetTimers();

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      if (mousemoveThrottleRef.current) {
        clearTimeout(mousemoveThrottleRef.current);
        mousemoveThrottleRef.current = null;
      }
    };
  }, [enabled, resetTimers]);

  return {
    showWarning,
    reset,
  };
};

